/**
 * src/hooks/useNavTracking.js
 *
 * Tracks navigation usage per user and computes the "My Menu" smart section:
 *   - Pinned items (explicit, user-chosen)
 *   - 2 most-used items (by visit count, excluding pinned)
 *   - 3 most-recent items (by last visit, excluding pinned + most-used)
 *   - No duplicates across any of the three lists
 *
 * Data lives on user.sidebarPrefs.pinnedItems / navUsage and persists via
 * the existing persistSidebarPrefs() flow → persistUsers() → Azure SQL.
 */
import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { NAV_GROUPS, SYSTEM_NAV } from '../config/navConfig.js';

// ── Flat lookup of every navigable item ──────────────────────────────────────

const ALL_NAV_ITEMS = (() => {
  const items = [];
  for (const group of NAV_GROUPS) {
    if (group.subGroups) {
      for (const sg of group.subGroups) items.push(...sg.items);
    } else {
      items.push(...group.items);
    }
  }
  items.push(...SYSTEM_NAV);
  items.push({ key: 'settings', path: '/settings', icon: 'ᛋ', label: 'Settings' });
  return items;
})();

const KEY_TO_ITEM = Object.fromEntries(ALL_NAV_ITEMS.map((i) => [i.key, i]));

function findItemByPath(pathname) {
  // Exact match first, then prefix match (e.g. /controls/SCF-123 → /controls)
  for (const item of ALL_NAV_ITEMS) {
    if (pathname === item.path) return item;
  }
  for (const item of ALL_NAV_ITEMS) {
    if (pathname.startsWith(item.path + '/')) return item;
  }
  return null;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @param {object}   sidebarPrefs        – current user's sidebarPrefs object
 * @param {function} persistSidebarPrefs – callback to merge+persist prefs (debounced)
 * @param {function} filterFn            – permission filter: (items) => allowed items
 */
export function useNavTracking(sidebarPrefs, persistSidebarPrefs, filterFn) {
  const location = useLocation();
  const prevPathRef = useRef(null);
  // Use a ref so the tracking effect doesn't re-fire when prefs change
  // (which would create an infinite loop since tracking updates prefs).
  const prefsRef = useRef(sidebarPrefs);
  useEffect(() => {
    prefsRef.current = sidebarPrefs;
  }, [sidebarPrefs]);

  // ── Track navigation on route change ──
  useEffect(() => {
    const item = findItemByPath(location.pathname);
    if (!item || location.pathname === prevPathRef.current) return;

    const navUsage = { ...(prefsRef.current?.navUsage || {}) };
    const prev = navUsage[item.key] || { count: 0, lastVisited: 0 };
    navUsage[item.key] = {
      count: prev.count + 1,
      lastVisited: Date.now(),
    };
    // Only mark as tracked if persist was queued (session/users ready).
    // If not ready, the effect will re-run when persistSidebarPrefs recreates.
    if (persistSidebarPrefs({ navUsage }) !== false) {
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, persistSidebarPrefs]);

  // ── Compute smart menu ──
  const smartMenu = useMemo(() => {
    const pinnedKeys = sidebarPrefs?.pinnedItems || [];
    const navUsage = sidebarPrefs?.navUsage || {};
    const pinnedSet = new Set(pinnedKeys);

    // Pinned → resolve keys to item objects, then permission-filter
    const pinnedRaw = pinnedKeys.map((k) => KEY_TO_ITEM[k]).filter(Boolean);
    const pinned = filterFn ? filterFn(pinnedRaw) : pinnedRaw;

    // Candidates: items with usage data, not pinned, permission-allowed
    const candidatesRaw = Object.entries(navUsage)
      .filter(([key]) => !pinnedSet.has(key) && KEY_TO_ITEM[key])
      .map(([key, usage]) => ({
        ...KEY_TO_ITEM[key],
        _count: usage.count,
        _lastVisited: usage.lastVisited,
      }));
    const candidates = filterFn ? filterFn(candidatesRaw) : candidatesRaw;

    // Top 2 by visit count → "most used"
    const byCount = [...candidates].sort((a, b) => b._count - a._count);
    const mostUsed = byCount.slice(0, 2);
    const mostUsedKeys = new Set(mostUsed.map((i) => i.key));

    // Top 3 by recency from the remainder → "recent"
    const rest = candidates.filter((i) => !mostUsedKeys.has(i.key));
    const byTime = [...rest].sort((a, b) => b._lastVisited - a._lastVisited);
    const recent = byTime.slice(0, 3);

    return { pinned, mostUsed, recent };
  }, [sidebarPrefs?.pinnedItems, sidebarPrefs?.navUsage, filterFn]);

  // ── Pin / unpin ──
  const togglePin = useCallback(
    (itemKey) => {
      const pinned = [...(prefsRef.current?.pinnedItems || [])];
      const idx = pinned.indexOf(itemKey);
      if (idx >= 0) {
        pinned.splice(idx, 1);
      } else {
        if (pinned.length >= 10) return; // cap at 10 pins
        pinned.push(itemKey);
      }
      persistSidebarPrefs({ pinnedItems: pinned });
    },
    [persistSidebarPrefs]
  );

  const isPinned = useCallback(
    (itemKey) => (sidebarPrefs?.pinnedItems || []).includes(itemKey),
    [sidebarPrefs?.pinnedItems]
  );

  return { smartMenu, togglePin, isPinned };
}
